import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAdminAuth } from "./AdminAuthContext";
import { adminApi } from "./api";
import {
  LayoutDashboard,
  Package,
  Boxes,
  Warehouse,
  CalendarClock,
  ShoppingCart,
  Users,
  FileBarChart,
  ScrollText,
  Inbox,
  LogOut,
  ExternalLink,
} from "lucide-react";

const NAV_ITEMS = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "inventory_manager", "sales_manager"] },
  { to: "/admin/products", label: "Products", icon: Package, roles: ["super_admin", "inventory_manager"] },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes, roles: ["super_admin", "inventory_manager"] },
  { to: "/admin/warehouses", label: "Warehouses", icon: Warehouse, roles: ["super_admin", "inventory_manager"] },
  { to: "/admin/batches", label: "Batches & Expiry", icon: CalendarClock, roles: ["super_admin", "inventory_manager"] },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart, roles: ["super_admin", "sales_manager"] },
  { to: "/admin/distributors", label: "Distributors", icon: Users, roles: ["super_admin", "sales_manager"] },
  { to: "/admin/messages", label: "Messages", icon: Inbox, roles: ["super_admin", "sales_manager"] },
  { to: "/admin/reports", label: "Reports", icon: FileBarChart, roles: ["super_admin", "inventory_manager", "sales_manager"] },
  { to: "/admin/logs", label: "Activity Logs", icon: ScrollText, roles: ["super_admin"] },
];

const ROLE_LABEL = {
  super_admin: "Super Admin",
  inventory_manager: "Inventory Manager",
  sales_manager: "Sales Manager",
};

export default function AdminLayout() {
  const { user, loading, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    const fetchUnread = async () => {
      try {
        const { data } = await adminApi.get("/messages/summary");
        if (!mounted) return;
        const u = data.unread || {};
        setUnreadMessages((u.contact || 0) + (u.newsletter || 0) + (u.subscriptions || 0));
      } catch {
        /* ignore */
      }
    };
    fetchUnread();
    const i = setInterval(fetchUnread, 30000);
    const onChange = () => fetchUnread();
    window.addEventListener("admin-messages-read", onChange);
    return () => {
      mounted = false;
      clearInterval(i);
      window.removeEventListener("admin-messages-read", onChange);
    };
  }, [user]);

  // Toggle a body-level class so the white admin theme covers any overflow /
  // scroll area beyond the .admin-light wrapper (prevents dark bg leaking on
  // the right edge / horizontal overflow zones).
  useEffect(() => {
    document.body.classList.add("admin-body-light");
    document.documentElement.classList.add("admin-body-light");
    return () => {
      document.body.classList.remove("admin-body-light");
      document.documentElement.classList.remove("admin-body-light");
    };
  }, []);

  if (loading) return <div className="min-h-screen bg-ink-950 text-mist flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const visibleNav = NAV_ITEMS.filter((n) => n.roles.includes(user.role));

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="admin-light min-h-screen bg-ink-950 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-ink-900 border-r border-white/5 flex flex-col fixed inset-y-0 left-0 z-30">
        <div className="px-6 py-6 border-b border-white/5">
          <p className="font-serif text-2xl text-bone">
            Banana<span className="text-gold italic">X</span>
          </p>
          <p className="label-eyebrow text-mist mt-1">Operations Console</p>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto custom-scrollbar" data-testid="admin-sidebar-nav">
          {visibleNav.map(({ to, label, icon: Icon }) => {
            const badge = label === "Messages" && unreadMessages > 0 ? unreadMessages : null;
            return (
              <NavLink
                key={to}
                to={to}
                data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 text-sm font-light transition-colors ${
                    isActive
                      ? "bg-gold/10 text-gold border-l-2 border-gold"
                      : "text-bone/80 hover:bg-white/5 hover:text-bone border-l-2 border-transparent"
                  }`
                }
              >
                <Icon className="w-4 h-4 stroke-[1.5]" />
                <span className="flex-1">{label}</span>
                {badge != null && (
                  <span
                    className="ml-auto bg-burgundy text-bone text-[10px] font-medium px-1.5 py-0.5 rounded-full min-w-[20px] text-center tracking-wider"
                    data-testid="messages-unread-badge"
                  >
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-4 py-5 border-t border-white/5 space-y-3">
          <div className="text-xs">
            <p className="text-bone font-medium truncate">{user.email}</p>
            <p className="label-eyebrow text-gold mt-1">{ROLE_LABEL[user.role]}</p>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-mist hover:text-gold text-xs tracking-[0.18em] uppercase"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Visit storefront
          </a>
          <button
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 text-mist hover:text-rose-300 text-xs tracking-[0.18em] uppercase"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-64 min-h-screen w-[calc(100%-16rem)]">
        <div className="p-8 max-w-[1500px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
